import {useCallback, useRef, useState} from "react";

type Props = {
    onFilesDropped: (files: FileList | null) => void;
};

type DropzoneHandlers = {
    onDragEnter: React.DragEventHandler<HTMLElement>;
    onDragLeave: React.DragEventHandler<HTMLElement>;
    onDragOver: React.DragEventHandler<HTMLElement>;
    onDrop: React.DragEventHandler<HTMLElement>;
};

type Result = {
    dropzoneHandlers: DropzoneHandlers;
    isDragActive: boolean;
};

const useFileDropzone = ({onFilesDropped}: Props): Result => {
    const [isDragActive, setIsDragActive] = useState(false);
    const dragDepthRef = useRef(0);

    const hasFileDrag = useCallback((event: React.DragEvent<HTMLElement>) => {
        return Array.from(event.dataTransfer.types).includes("Files");
    }, []);

    const handleDragEnter = useCallback((event: React.DragEvent<HTMLElement>) => {
        if (!hasFileDrag(event)) {
            return;
        }

        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDragActive(true);
    }, [hasFileDrag]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
        if (!hasFileDrag(event)) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setIsDragActive(true);
    }, [hasFileDrag]);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
        if (!hasFileDrag(event)) {
            return;
        }

        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragActive(false);
        }
    }, [hasFileDrag]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLElement>) => {
        if (!hasFileDrag(event)) {
            return;
        }

        event.preventDefault();
        dragDepthRef.current = 0;
        setIsDragActive(false);
        onFilesDropped(event.dataTransfer.files);
    }, [hasFileDrag, onFilesDropped]);

    return {
        dropzoneHandlers: {
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver,
            onDrop: handleDrop,
        },
        isDragActive,
    };
};

export default useFileDropzone;
